import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, ShieldCheck } from 'lucide-react';
import { useWarranties, warrantyDaysRemaining, groupWarrantiesByComponent } from '@/hooks/useWarranties';
import type { Warranty } from '@/hooks/useWarranties';

const COMPONENT_LABELS: Record<string, string> = {
  panels: 'Panels',
  inverter: 'Inverter',
  battery: 'Battery',
  generator: 'Generator',
  workmanship: 'Workmanship',
  third_party: 'Third Party',
};

function statusBadge(w: Warranty) {
  const days = warrantyDaysRemaining(w.warranty_end_date);
  if (w.status === 'expired' || (days !== null && days < 0)) {
    return <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">Expired</Badge>;
  }
  if (days !== null && days <= 30) {
    return <Badge variant="outline" className="text-[10px] bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20">Expiring soon</Badge>;
  }
  if (w.status === 'claimed') {
    return <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">Claimed</Badge>;
  }
  if (w.status === 'active') {
    return <Badge variant="outline" className="text-[10px] bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20">Active</Badge>;
  }
  return <Badge variant="outline" className="text-[10px]">{w.status}</Badge>;
}

function formatEndDate(d: string | null): string {
  if (!d) return 'No end date';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysLabel(d: string | null): string | null {
  const days = warrantyDaysRemaining(d);
  if (days === null) return null;
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d remaining`;
}

interface Props {
  propertyId: string;
  onAddClick: () => void;
}

export function WarrantyRegistryCard({ propertyId, onAddClick }: Props) {
  const { data: warranties, isLoading } = useWarranties(propertyId);
  const grouped = groupWarrantiesByComponent(warranties ?? []);
  const componentTypes = Object.keys(grouped);

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" />
            Warranty Registry
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onAddClick}>
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading warranties...</p>
        ) : componentTypes.length === 0 ? (
          <p className="text-xs text-muted-foreground">No warranties recorded</p>
        ) : (
          componentTypes.map(type => (
            <div key={type}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                {COMPONENT_LABELS[type] ?? type}
              </p>
              {grouped[type].map(w => (
                <Link
                  key={w.id}
                  to={`/app/properties/${propertyId}/warranties/${w.id}`}
                  className="flex items-center justify-between text-xs hover:bg-muted/50 rounded px-2 py-1.5 -mx-2"
                >
                  <span className="truncate">
                    {w.provider_name}{w.model ? ` — ${w.model}` : ''}
                    {w.term_years ? ` (${w.term_years}yr)` : ''}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0 ml-2">
                    {daysLabel(w.warranty_end_date) && (
                      <span className="text-[10px] text-muted-foreground">{daysLabel(w.warranty_end_date)}</span>
                    )}
                    {statusBadge(w)}
                  </span>
                </Link>
              ))}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
